/**
 * Update Notification Banner
 * Shows when a new app version is available (Electron/Capacitor only)
 */

import { useState, useEffect } from 'react';
import { Download, RefreshCw, X } from 'lucide-react';
import { isPWA, getUpdateAdapter } from '@/services/platform';
import type { UpdateInfo } from '@/services/platform';

export function UpdateNotification() {
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isPWA()) return;

    const adapter = getUpdateAdapter();
    adapter.onUpdateAvailable((info) => {
      setUpdateInfo(info);
    });

    // Listen for download complete (Electron)
    const cleanup = window.electronAPI?.on('updater:update-downloaded', (data) => {
      const info = data as { version: string };
      setUpdateInfo({ version: info.version });
      setIsDownloaded(true);
    });

    return () => { cleanup?.(); };
  }, []);

  if (!updateInfo || dismissed || isPWA()) return null;

  const handleInstall = async () => {
    const adapter = getUpdateAdapter();
    await adapter.installUpdate();
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex items-center gap-3 bg-blue-600 text-white rounded-lg px-4 py-3 shadow-xl max-w-sm">
      {isDownloaded ? (
        <RefreshCw size={18} className="shrink-0" />
      ) : (
        <Download size={18} className="shrink-0 animate-bounce" />
      )}
      <div className="flex-1 text-sm">
        {isDownloaded ? (
          <span>HighFive v{updateInfo.version} is ready.</span>
        ) : (
          <span>Downloading v{updateInfo.version}...</span>
        )}
      </div>
      {isDownloaded && (
        <button
          onClick={handleInstall}
          className="px-3 py-1 bg-white text-blue-600 text-xs font-semibold rounded hover:bg-blue-50 transition-colors"
        >
          Restart
        </button>
      )}
      <button onClick={() => setDismissed(true)} className="p-0.5 hover:bg-white/20 rounded">
        <X size={14} />
      </button>
    </div>
  );
}
